import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const PageContainer = styled.main`
  min-height: 100vh;
  background: linear-gradient(180deg, #f8faff 0%, #fff5fb 100%);
  padding: clamp(32px, 6vw, 64px) clamp(16px, 6vw, 80px);
  display: grid;
  gap: clamp(32px, 4vw, 48px);
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
  gap: clamp(24px, 4vw, 40px);

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const BoardCard = styled.section`
  background: rgba(255, 255, 255, 0.92);
  border-radius: 24px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.08);
  padding: clamp(20px, 3vw, 28px);
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 4vw, 2.6rem);
  color: #111827;
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6b7280;
  line-height: 1.5;
`;

const TopicList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TopicButton = styled.button`
  all: unset;
  padding: 14px 16px;
  border-radius: 16px;
  cursor: pointer;
  background: ${(props) => (props.active ? 'linear-gradient(135deg, #6366f1, #ec4899)' : 'rgba(99, 102, 241, 0.08)')};
  color: ${(props) => (props.active ? '#ffffff' : '#312e81')};
  font-weight: 600;
  display: grid;
  gap: 6px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-1px);
    box-shadow: 0 10px 18px rgba(99, 102, 241, 0.18);
  }
`;

const TopicMeta = styled.span`
  font-size: 0.85rem;
  opacity: 0.8;
`;

const TopicDetail = styled.article`
  display: flex;
  flex-direction: column;
  gap: clamp(18px, 3vw, 28px);
`;

const TopicBody = styled.div`
  background: rgba(255, 255, 255, 0.94);
  border-radius: 24px;
  padding: clamp(24px, 4vw, 32px);
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.08);
  color: #1f2937;
  line-height: 1.7;
  white-space: pre-line;
`;

const ReplyList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ReplyItem = styled.li`
  padding: 16px 20px;
  border-radius: 18px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(129, 140, 248, 0.3);
`;

const ReplyAuthor = styled.h3`
  margin: 0 0 6px;
  font-size: 0.95rem;
  color: #4338ca;
`;

const ReplyBody = styled.p`
  margin: 0;
  line-height: 1.6;
  color: #1f2937;
`;

const FormCard = styled.form`
  background: rgba(255, 255, 255, 0.92);
  border-radius: 24px;
  padding: clamp(24px, 4vw, 32px);
  box-shadow: 0 16px 44px rgba(15, 23, 42, 0.08);
  display: grid;
  gap: 16px;
`;

const FormTitle = styled.h2`
  margin: 0;
  color: #1f2937;
`;

const InputGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #111827;
`;

const TextInput = styled.input`
  border-radius: 14px;
  border: 1px solid rgba(99, 102, 241, 0.25);
  padding: 12px 14px;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.95);

  &:focus-visible {
    outline: 2px solid rgba(236, 72, 153, 0.4);
    outline-offset: 2px;
  }
`;

const TextArea = styled.textarea`
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.25);
  padding: 14px;
  min-height: 160px;
  resize: vertical;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.95);

  &:focus-visible {
    outline: 2px solid rgba(236, 72, 153, 0.4);
    outline-offset: 2px;
  }
`;

const SubmitButton = styled.button`
  justify-self: flex-start;
  padding: 12px 24px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #22d3ee, #6366f1);
  color: white;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-1px);
  }
`;

const StatusMessage = styled.p`
  margin: 0;
  color: ${(props) => (props.error ? '#ef4444' : '#059669')};
  font-weight: 600;
`;

const HelperText = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 0.9rem;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const DeleteButton = styled.button`
  justify-self: flex-start;
  padding: 10px 18px;
  border-radius: 999px;
  border: 1px solid rgba(239, 68, 68, 0.4);
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-1px);
    background: rgba(239, 68, 68, 0.14);
  }
`;

const EmptyState = styled.p`
  color: #6b7280;
  text-align: center;
`;

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('ko', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const CommunityBoardPage = () => {
  const [topics, setTopics] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loadingTopic, setLoadingTopic] = useState(false);
  const [postStatus, setPostStatus] = useState({ message: '', error: false });
  const [replyStatus, setReplyStatus] = useState({ message: '', error: false });
  const [topicForm, setTopicForm] = useState({ title: '', author: '', body: '', deleteKey: '' });
  const [replyForm, setReplyForm] = useState({ author: '', body: '', deleteKey: '' });

  useEffect(() => {
    const loadTopics = async () => {
      try {
        const response = await fetch('/api/community?resource=topics');
        if (!response.ok) {
          throw new Error('커뮤니티 글을 불러오지 못했습니다.');
        }
        const data = await response.json();
        setTopics(data.topics || []);
        if ((data.topics || []).length > 0) {
          setSelectedId(data.topics[0].id);
        }
      } catch (error) {
        setPostStatus({ message: error.message, error: true });
      }
    };

    loadTopics();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSelectedTopic(null);
      setReplies([]);
      return;
    }

    const loadTopic = async () => {
      setLoadingTopic(true);
      try {
        const response = await fetch(`/api/community?resource=topic&id=${selectedId}`);
        if (!response.ok) {
          throw new Error('게시글을 불러오지 못했습니다.');
        }
        const data = await response.json();
        setSelectedTopic(data.topic);
        setReplies(data.replies || []);
      } catch (error) {
        setReplyStatus({ message: error.message, error: true });
      } finally {
        setLoadingTopic(false);
      }
    };

    loadTopic();
  }, [selectedId]);

  const handleTopicFormChange = (event) => {
    const { name, value } = event.target;
    setTopicForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleReplyFormChange = (event) => {
    const { name, value } = event.target;
    setReplyForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitTopic = async (event) => {
    event.preventDefault();
    setPostStatus({ message: '', error: false });

    try {
      const response = await fetch('/api/community?resource=topics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(topicForm),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || '게시글을 등록하지 못했습니다.');
      }

      if (data?.topic?.id) {
        setTopics((prev) => [{ ...data.topic, reply_count: 0 }, ...prev]);
        setSelectedId(data.topic.id);
        setSelectedTopic(data.topic);
      }

      setPostStatus({ message: data?.message || '게시글이 등록되었습니다.', error: false });
      setTopicForm({ title: '', author: '', body: '', deleteKey: '' });
    } catch (error) {
      setPostStatus({ message: error.message, error: true });
    }
  };

  const submitReply = async (event) => {
    event.preventDefault();
    if (!selectedId) return;
    setReplyStatus({ message: '', error: false });

    try {
      const response = await fetch('/api/community?resource=replies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...replyForm, topicId: selectedId }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || '댓글을 등록하지 못했습니다.');
      }

      if (data?.reply?.id) {
        setReplies((prev) => [...prev, data.reply]);
        setTopics((prev) =>
          prev.map((topic) =>
            topic.id === selectedId
              ? { ...topic, reply_count: Math.max((topic.reply_count || 0) + 1, 0) }
              : topic
          )
        );
      }

      setReplyStatus({ message: data?.message || '댓글이 등록되었습니다.', error: false });
      setReplyForm({ author: '', body: '', deleteKey: '' });
    } catch (error) {
      setReplyStatus({ message: error.message, error: true });
    }
  };

  const handleDeleteTopic = async () => {
    if (!selectedId) return;
    const deleteKey = window.prompt('게시글 삭제 키를 입력해 주세요.');
    if (!deleteKey) return;

    try {
      const response = await fetch('/api/community?resource=topics', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topicId: selectedId, deleteKey }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || '게시글을 삭제하지 못했습니다.');
      }

      setTopics((prev) => {
        const updated = prev.filter((topic) => topic.id !== selectedId);
        const nextId = updated[0]?.id || null;
        setSelectedId(nextId);
        return updated;
      });
      setSelectedTopic(null);
      setReplies([]);
      setPostStatus({ message: data?.message || '게시글이 삭제되었습니다.', error: false });
    } catch (error) {
      setPostStatus({ message: error.message, error: true });
    }
  };

  const handleDeleteReply = async (replyId) => {
    const deleteKey = window.prompt('댓글 삭제 키를 입력해 주세요.');
    if (!deleteKey) return;

    try {
      const response = await fetch('/api/community?resource=replies', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ replyId, deleteKey }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || '댓글을 삭제하지 못했습니다.');
      }

      setReplies((prev) => prev.filter((reply) => reply.id !== replyId));
      setTopics((prev) =>
        prev.map((topic) =>
          topic.id === selectedId
            ? { ...topic, reply_count: Math.max((topic.reply_count || 1) - 1, 0) }
            : topic
        )
      );
      setReplyStatus({ message: data?.message || '댓글이 삭제되었습니다.', error: false });
    } catch (error) {
      setReplyStatus({ message: error.message, error: true });
    }
  };

  return (
    <PageContainer>
      <BoardCard>
        <Title>애니칠리 커뮤니티</Title>
        <Subtitle>
          검토 없이 바로 올라가는 익명 커뮤니티예요. 원하는 만큼 올리고, 저장해 둔 삭제 키로 언제든 스스로
          지울 수 있어요. 문제가 생기면 관리자가 개입할 수 있습니다.
        </Subtitle>
      </BoardCard>

      <Layout>
        <BoardCard aria-label="커뮤니티 글 목록">
          <h2>최근 글</h2>
          {topics.length === 0 ? (
            <EmptyState>아직 공개된 글이 없습니다.</EmptyState>
          ) : (
            <TopicList>
              {topics.map((topic) => (
                <li key={topic.id}>
                  <TopicButton
                    type="button"
                    active={topic.id === selectedId}
                    onClick={() => setSelectedId(topic.id)}
                  >
                    <span>{topic.title}</span>
                    <TopicMeta>
                      {topic.author} · {formatDate(topic.created_at)} · 댓글 {topic.reply_count || 0}
                    </TopicMeta>
                  </TopicButton>
                </li>
              ))}
            </TopicList>
          )}
        </BoardCard>

        <TopicDetail>
          <BoardCard aria-live="polite">
            {loadingTopic && <EmptyState>게시글을 불러오는 중입니다...</EmptyState>}
            {!loadingTopic && selectedTopic && (
              <> 
                <h2>{selectedTopic.title}</h2>
                <TopicMeta>
                  {selectedTopic.author} · {formatDate(selectedTopic.created_at)}
                </TopicMeta>
                <TopicBody>{selectedTopic.body}</TopicBody>
                <ButtonRow>
                  <DeleteButton type="button" onClick={handleDeleteTopic}>
                    게시글 삭제
                  </DeleteButton>
                </ButtonRow>
              </>
            )}
            {!loadingTopic && !selectedTopic && (
              <EmptyState>확인할 게시글을 선택해 주세요.</EmptyState>
            )}
          </BoardCard>

          <BoardCard>
            <h2>댓글</h2>
            {replies.length > 0 ? (
              <ReplyList>
                {replies.map((reply) => (
                  <ReplyItem key={reply.id}>
                    <ReplyAuthor>{reply.author}</ReplyAuthor>
                    <ReplyBody>{reply.body}</ReplyBody>
                    <TopicMeta>{formatDate(reply.created_at)}</TopicMeta>
                    <ButtonRow>
                      <DeleteButton type="button" onClick={() => handleDeleteReply(reply.id)}>
                        댓글 삭제
                      </DeleteButton>
                    </ButtonRow>
                  </ReplyItem>
                ))}
              </ReplyList>
            ) : (
              <EmptyState>아직 공개된 댓글이 없습니다.</EmptyState>
            )}

            <FormCard onSubmit={submitReply}>
              <FormTitle>댓글 남기기</FormTitle>
              <InputGroup>
                <Label htmlFor="reply-author">닉네임</Label>
                <TextInput
                  id="reply-author"
                  name="author"
                  value={replyForm.author}
                  onChange={handleReplyFormChange}
                  maxLength={40}
                  placeholder="미입력 시 익명"
                />
                <HelperText>닉네임은 비워 두면 익명으로 표시돼요.</HelperText>
              </InputGroup>
              <InputGroup>
                <Label htmlFor="reply-body">내용</Label>
                <TextArea
                  id="reply-body"
                  name="body"
                  value={replyForm.body}
                  onChange={handleReplyFormChange}
                  maxLength={2000}
                  required
                />
              </InputGroup>
              <InputGroup>
                <Label htmlFor="reply-delete-key">삭제 키</Label>
                <TextInput
                  id="reply-delete-key"
                  name="deleteKey"
                  type="password"
                  value={replyForm.deleteKey}
                  onChange={handleReplyFormChange}
                  maxLength={64}
                  required
                />
                <HelperText>작성자가 스스로 댓글을 지울 때 사용할 키예요. 꼭 기억해 주세요.</HelperText>
              </InputGroup>
              <SubmitButton type="submit">댓글 등록</SubmitButton>
              {replyStatus.message && (
                <StatusMessage error={replyStatus.error}>{replyStatus.message}</StatusMessage>
              )}
            </FormCard>
          </BoardCard>
        </TopicDetail>
      </Layout>

      <FormCard onSubmit={submitTopic} aria-label="새 글 작성">
        <FormTitle>새 글 작성</FormTitle>
        <Subtitle>서로를 배려하는 언어로 자유롭게 이야기해 주세요. 삭제 키를 잊지 말고 메모해 두세요.</Subtitle>
        <InputGroup>
          <Label htmlFor="topic-title">제목</Label>
          <TextInput
            id="topic-title"
            name="title"
            value={topicForm.title}
            onChange={handleTopicFormChange}
            maxLength={120}
            required
          />
        </InputGroup>
        <InputGroup>
          <Label htmlFor="topic-author">닉네임</Label>
          <TextInput
            id="topic-author"
            name="author"
            value={topicForm.author}
            onChange={handleTopicFormChange}
            maxLength={40}
            placeholder="미입력 시 익명"
          />
          <HelperText>닉네임은 선택 입력이에요. 비워 두면 익명으로 표시돼요.</HelperText>
        </InputGroup>
        <InputGroup>
          <Label htmlFor="topic-body">내용</Label>
          <TextArea
            id="topic-body"
            name="body"
            value={topicForm.body}
            onChange={handleTopicFormChange}
            maxLength={4000}
            required
          />
        </InputGroup>
        <InputGroup>
          <Label htmlFor="topic-delete-key">삭제 키</Label>
          <TextInput
            id="topic-delete-key"
            name="deleteKey"
            type="password"
            value={topicForm.deleteKey}
            onChange={handleTopicFormChange}
            maxLength={64}
            required
          />
          <HelperText>작성자가 스스로 글을 지울 때 사용할 키예요. 분실 시 복구할 수 없어요.</HelperText>
        </InputGroup>
        <SubmitButton type="submit">글 등록</SubmitButton>
        {postStatus.message && <StatusMessage error={postStatus.error}>{postStatus.message}</StatusMessage>}
      </FormCard>
    </PageContainer>
  );
};

export default CommunityBoardPage;
